import React from 'react'
import { Link } from 'react-router-dom'
import { Box, ListItemButton, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import InfoIcon from '@mui/icons-material/Info'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import type { AppNotification, NotificationSeverity } from '../../types/notifications'
import { formatRelativeTimestamp } from '../../utils/dateFormatters'
import { notificationTitle } from './notificationLabels'

const TEAL = '#007f98'
const CHARCOAL = '#333333'

const SEVERITY_ICON: Record<NotificationSeverity, React.ReactElement> = {
  success: <CheckCircleIcon fontSize="small" color="success" />,
  error: <ErrorIcon fontSize="small" color="error" />,
  warning: <WarningAmberIcon fontSize="small" color="warning" />,
  info: <InfoIcon fontSize="small" color="info" />,
}

interface NotificationItemProps {
  notification: AppNotification
  onActivate: (id: number) => void
}

/**
 * One row in the bell dropdown:
 *   [severity icon] [title (bold if unread) • dot] .......... [relative time]
 *                   [context line, muted]                    [chevron →]
 * When `action_url` is set the row IS a react-router <Link> (RULES nav -
 * owned by the link, not an onClick); the onClick only marks the row seen.
 */
const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onActivate }) => {
  const { id, category, severity, title, message, action_url, created_at, seen_at } =
    notification
  const time = formatRelativeTimestamp(created_at)
  const unseen = !seen_at
  const displayTitle = notificationTitle(category, title)

  const linkProps = action_url
    ? { component: Link as React.ElementType, to: action_url }
    : {}

  return (
    <ListItemButton
      {...linkProps}
      onClick={() => onActivate(id)}
      alignItems="flex-start"
      sx={{
        px: 2,
        py: 1.5,
        borderLeft: unseen ? `3px solid ${TEAL}` : '3px solid transparent',
        borderBottom: '1px solid #EEE',
        // Seen rows fade back so unread clearly stands out; hover restores
        // full strength so a faded row stays legible when you reach for it.
        opacity: unseen ? 1 : 0.6,
        transition: 'opacity 120ms ease',
        '&:hover': { backgroundColor: '#F5F7F8', opacity: 1 },
      }}
    >
      <Box sx={{ mt: 0.25, mr: 1.5, display: 'flex', flexShrink: 0 }}>
        {SEVERITY_ICON[severity] ?? SEVERITY_ICON.info}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ flex: 1, fontWeight: unseen ? 600 : 400, color: unseen ? CHARCOAL : '#666' }}
          >
            {displayTitle}
          </Typography>
          {unseen && (
            <Box
              sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TEAL, flexShrink: 0 }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
          {message}
        </Typography>
      </Box>

      <Box
        sx={{
          ml: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          flexShrink: 0,
        }}
      >
        <Typography variant="caption" color="text.disabled" noWrap title={time.title}>
          {time.label}
        </Typography>
        {action_url && <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
      </Box>
    </ListItemButton>
  )
}

export default NotificationItem
